import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Bell,
  BellRing,
  Check,
  X,
  Clock,
  CreditCard,
  Users,
  DollarSign,
  Gift,
  AlertCircle,
  CheckCircle,
  Info,
  Settings,
  Filter,
  MoreVertical,
  Trash2,
  MarkAsRead,
  Archive
} from 'lucide-react';

const MemberNotifications = ({ user, onMarkAsRead, onDeleteNotification, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const notificationTypes = {
    payment_reminder: { icon: CreditCard, color: 'text-orange-400 bg-orange-500/20', priority: 'high' },
    contribution_confirmed: { icon: CheckCircle, color: 'text-green-400 bg-green-500/20', priority: 'medium' },
    payout_received: { icon: DollarSign, color: 'text-blue-400 bg-blue-500/20', priority: 'high' },
    group_join_approved: { icon: Users, color: 'text-purple-400 bg-purple-500/20', priority: 'medium' },
    cycle_complete: { icon: Gift, color: 'text-pink-400 bg-pink-500/20', priority: 'medium' },
    system_update: { icon: Info, color: 'text-cyan-400 bg-cyan-500/20', priority: 'low' },
    crypto_contribution: { icon: CreditCard, color: 'text-yellow-400 bg-yellow-500/20', priority: 'high' },
    join_request: { icon: Users, color: 'text-indigo-400 bg-indigo-500/20', priority: 'medium' }
  };

  const filters = [
    { id: 'all', label: 'All', count: 0 },
    { id: 'unread', label: 'Unread', count: 0 },
    { id: 'high', label: 'Important', count: 0 },
    { id: 'payments', label: 'Payments', count: 0 },
    { id: 'groups', label: 'Groups', count: 0 }
  ];

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data || []);
        setLoading(false);
      } else {
        console.error('Failed to fetch notifications');
        setNotifications([]);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setLoading(false);
    }
  };

  const getFilteredNotifications = () => {
    let filtered = notifications;
    
    switch (filter) {
      case 'unread':
        filtered = notifications.filter(n => !n.is_read);
        break;
      case 'high':
        filtered = notifications.filter(n => n.priority === 'high');
        break;
      case 'payments':
        filtered = notifications.filter(n => ['payment_reminder', 'contribution_confirmed', 'payout_received', 'crypto_contribution'].includes(n.type));
        break;
      case 'groups':
        filtered = notifications.filter(n => ['group_join_approved', 'join_request', 'cycle_complete'].includes(n.type));
        break;
      default:
        filtered = notifications;
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const getFilterCounts = () => {
    return {
      all: notifications.length,
      unread: notifications.filter(n => !n.is_read).length,
      high: notifications.filter(n => n.priority === 'high').length,
      payments: notifications.filter(n => ['payment_reminder', 'contribution_confirmed', 'payout_received', 'crypto_contribution'].includes(n.type)).length,
      groups: notifications.filter(n => ['group_join_approved', 'join_request', 'cycle_complete'].includes(n.type)).length
    };
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      if (onMarkAsRead) {
        await onMarkAsRead(notificationId);
      }
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      if (onDeleteNotification) {
        await onDeleteNotification(notificationId);
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleBulkAction = async (action) => {
    try {
      for (const id of selectedNotifications) {
        if (action === 'read') {
          await handleMarkAsRead(id);
        } else if (action === 'delete') {
          await handleDeleteNotification(id);
        }
      }
      setSelectedNotifications([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { color: 'bg-red-500/20 text-red-300', label: 'High' },
      medium: { color: 'bg-yellow-500/20 text-yellow-300', label: 'Medium' },
      low: { color: 'bg-gray-500/20 text-gray-300', label: 'Low' }
    };
    
    return badges[priority] || badges.low;
  };

  const filteredNotifications = getFilteredNotifications();
  const filterCounts = getFilterCounts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Notifications</h1>
            <p className="text-slate-300">
              Stay updated with your tontine activities and important alerts
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-3 bg-white/10 rounded-full backdrop-blur-sm hover:bg-white/20 transition-all"
            >
              <Settings className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center space-x-2">
              <span className="text-slate-300">{filterCounts.unread}</span>
              <Bell className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Filter className="w-5 h-5" />
                  <span>Filter Notifications</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {filters.map((filterOption) => (
                  <button
                    key={filterOption.id}
                    onClick={() => setFilter(filterOption.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      filter === filterOption.id 
                        ? 'bg-blue-600/30 text-blue-300' 
                        : 'hover:bg-white/10 text-slate-300'
                    }`}
                  >
                    <span>{filterOption.label}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      filter === filterOption.id 
                        ? 'bg-blue-500/30 text-blue-300' 
                        : 'bg-white/10 text-slate-400'
                    }`}>
                      {filterCounts[filterOption.id]}
                    </span>
                  </button>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            {selectedNotifications.length > 0 && (
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white mt-6">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {selectedNotifications.length} Selected
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <button
                    onClick={() => handleBulkAction('read')}
                    className="w-full flex items-center space-x-2 p-2 text-green-300 hover:bg-green-500/20 rounded-lg transition-all"
                  >
                    <Check className="w-4 h-4" />
                    <span>Mark as Read</span>
                  </button>
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="w-full flex items-center space-x-2 p-2 text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                  <button
                    onClick={() => setSelectedNotifications([])}
                    className="w-full flex items-center space-x-2 p-2 text-slate-300 hover:bg-white/10 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Notifications List */}
          <div className="lg:col-span-3">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {filter === 'all' ? 'All Notifications' : 
                     filter === 'unread' ? 'Unread Notifications' :
                     filter === 'high' ? 'Important Notifications' :
                     filter === 'payments' ? 'Payment Notifications' :
                     'Group Notifications'}
                  </CardTitle>
                  {filteredNotifications.length > 0 && (
                    <button
                      onClick={() => {
                        if (selectedNotifications.length === filteredNotifications.length) {
                          setSelectedNotifications([]);
                        } else {
                          setSelectedNotifications(filteredNotifications.map(n => n.id));
                        }
                      }}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      {selectedNotifications.length === filteredNotifications.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                ) : filteredNotifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-300 mb-2">No notifications found</p>
                    <p className="text-sm text-slate-500">You're all caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredNotifications.map((notification) => {
                      const typeConfig = notificationTypes[notification.type] || notificationTypes.system_update;
                      const IconComponent = typeConfig.icon;
                      const priorityBadge = getPriorityBadge(notification.priority);
                      const isSelected = selectedNotifications.includes(notification.id);

                      return (
                        <div
                          key={notification.id}
                          className={`p-4 rounded-lg transition-all cursor-pointer ${
                            notification.is_read 
                              ? 'bg-white/5 hover:bg-white/10' 
                              : 'bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20'
                          } ${
                            isSelected ? 'ring-2 ring-blue-500/50' : ''
                          }`}
                        >
                          <div className="flex items-start space-x-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedNotifications(prev => [...prev, notification.id]);
                                } else {
                                  setSelectedNotifications(prev => prev.filter(id => id !== notification.id));
                                }
                              }}
                              className="mt-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            
                            <div className={`p-2 rounded-full ${typeConfig.color}`}>
                              <IconComponent className="w-5 h-5" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className={`text-lg font-medium ${notification.is_read ? 'text-slate-300' : 'text-white'}`}>
                                  {notification.title}
                                </h4>
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 py-1 rounded-full text-xs ${priorityBadge.color}`}>
                                    {priorityBadge.label}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatTimeAgo(notification.created_at)}
                                  </span>
                                </div>
                              </div>
                              
                              <p className={`text-sm ${notification.is_read ? 'text-slate-400' : 'text-slate-300'} mb-3`}>
                                {notification.message}
                              </p>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  {!notification.is_read && (
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                                  )}
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                  {!notification.is_read && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsRead(notification.id);
                                      }}
                                      className="p-1 text-green-400 hover:bg-green-500/20 rounded transition-all"
                                      title="Mark as read"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteNotification(notification.id);
                                    }}
                                    className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-all"
                                    title="Delete notification"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberNotifications;